import { useEffect } from 'react';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ProductCard from '../ProductCard';
import { MultiVariantProductCard } from './MultiVariantProductCard';

import Button from '../Button';
import { H3 } from '../Typography';
import Breakpoints from '../../design-utils/breakpoints';
import { sortProductsBySelected } from '../../subscriptions/BundleUpdate';
import {
  getProductCurrentPrice,
  getProductPrice,
  getPriceFromSellingPlanAllocations,
  isGlobalProductList,
  isProductAvailable,
} from '../../utils';

const DEFAULT_SECTION = 'default';

export interface ProductsHandlerProps {
  bundleType: string;
  productsInCollection: GlobalVariant[] | GlobalProduct[];
  addProduct: (productId: GlobalVariant | GlobalProduct) => void;
  removeProduct: (productId: GlobalVariant | GlobalProduct) => void;
  selectedProducts: Record<string, number>;
  setSelectedProducts: (newSelectedProducts: Record<string, number>) => void;
  initialSelectedProducts: Record<string, number>;
  canAddProducts: boolean;
  hasSections?: boolean;
  sellingPlan?: string | number;
  discount?: {
    type: string;
    value: number;
  };
  onCardClick: (product) => void;
  shopConfiguration?: ShopConfiguration;
  hideBaBSoldOutOptions: boolean;
  ignoreBaBOutOfStockAllowance: boolean;
}

export const BundleSectionProducts = ({
  bundleType,
  productsInCollection,
  addProduct,
  removeProduct,
  selectedProducts,
  setSelectedProducts,
  canAddProducts,
  onCardClick,
  discount,
  sellingPlan,
  initialSelectedProducts,
  shopConfiguration,
  hideBaBSoldOutOptions,
  ignoreBaBOutOfStockAllowance,
}: ProductsHandlerProps): JSX.Element => {
  const { t } = useTranslation();
  const BaBProductsToCartFlag = bundleType === 'dynamicBaB';
  const isClassicBab = bundleType === 'buildABox';

  const sections = chunkProductsInSections(productsInCollection);
  useTitlesOnScreen(Object.keys(sections));
  const { [DEFAULT_SECTION]: others, ...productsSections } = sections;
  const objectsInSections = Object.entries(productsSections)?.map(
    ([section, products]) => {
      const sortedProducts = sortProductsBySelected(
        products,
        initialSelectedProducts,
      );
      return (
        <div id={`section-${section}`} key={`section-${section}`}>
          <SectionTitle
            key={`title-${section}`}
            id={section}
            modifiers={['display']}
          >
            {section}
          </SectionTitle>
          <SectionsSection key={section}>
            {isGlobalProductList(sortedProducts) ? (
              <MultiVariantProductCard
                discount={discount}
                sellingPlan={sellingPlan}
                products={sortedProducts}
                onCardClick={onCardClick}
                addProduct={addProduct}
                removeProduct={removeProduct}
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
                canAddProducts={canAddProducts}
                baBProductsToCartFlag={BaBProductsToCartFlag}
                initialSelectedProducts={initialSelectedProducts}
                hideBaBSoldOutOptions={hideBaBSoldOutOptions}
                ignoreBaBOutOfStockAllowance={ignoreBaBOutOfStockAllowance}
                isClassicBab={isClassicBab}
                ProductCard={ProductCard}
              />
            ) : (
              sortedProducts.map((product) => {
                let price;
                let currentPrice;

                if (BaBProductsToCartFlag) {
                  currentPrice = getProductCurrentPrice(product);
                  price =
                    getProductPrice(product, discount) ||
                    getPriceFromSellingPlanAllocations(product, sellingPlan) ||
                    currentPrice;
                }

                return (
                  <ProductCard
                    key={product.id}
                    id={product.id as number}
                    handle={product.handle}
                    productId={product.productId}
                    price={price}
                    currentPrice={currentPrice}
                    currency={get(
                      window,
                      ['Shopify', 'currency', 'active'],
                      'USD',
                    )}
                    image={product.image as string}
                    quantity={selectedProducts[product.id] || 0}
                    subtitle={product.metafields?.bundle?.subtitle}
                    title={product.name}
                    available={isProductAvailable(
                      product,
                      shopConfiguration,
                      ignoreBaBOutOfStockAllowance,
                    )}
                    onIncrease={() => canAddProducts && addProduct(product)}
                    onDecrease={() => removeProduct(product)}
                    onCardClick={(productInfo) =>
                      onCardClick({
                        ...productInfo,
                        images: product.images,
                        productId: product.productId,
                        hasOneVariant: product.hasOnlyDefaultVariant,
                        media: product.media,
                      })
                    }
                    description={product.description}
                    reviews={product?.reviews}
                  />
                );
              })
            )}
          </SectionsSection>
        </div>
      );
    },
  );

  if (others?.length) {
    objectsInSections.push(
      <div id={`section-${DEFAULT_SECTION}`} key={`section-${DEFAULT_SECTION}`}>
        <SectionTitle
          key={`title-${DEFAULT_SECTION}`}
          id={DEFAULT_SECTION}
          modifiers={['display']}
        >
          {t('bundles.additional_products')}
        </SectionTitle>
        <SectionsSection key={DEFAULT_SECTION}>
          {isGlobalProductList(others) ? (
            <MultiVariantProductCard
              discount={discount}
              sellingPlan={sellingPlan}
              products={others}
              onCardClick={onCardClick}
              addProduct={addProduct}
              removeProduct={removeProduct}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
              canAddProducts={canAddProducts}
              baBProductsToCartFlag={BaBProductsToCartFlag}
              initialSelectedProducts={initialSelectedProducts}
              hideBaBSoldOutOptions={hideBaBSoldOutOptions}
              ignoreBaBOutOfStockAllowance={ignoreBaBOutOfStockAllowance}
              isClassicBab={isClassicBab}
              ProductCard={ProductCard}
            />
          ) : (
            others?.map((product) => {
              let price;
              let currentPrice;

              if (BaBProductsToCartFlag) {
                currentPrice = getProductCurrentPrice(product);
                price =
                  getProductPrice(product, discount) ||
                  getPriceFromSellingPlanAllocations(product, sellingPlan) ||
                  currentPrice;
              }

              return (
                <ProductCard
                  price={price}
                  currentPrice={currentPrice}
                  currency={get(
                    window,
                    ['Shopify', 'currency', 'active'],
                    'USD',
                  )}
                  key={product.id}
                  productId={product.productId}
                  id={product.id as number}
                  handle={product.handle}
                  image={product.image as string}
                  quantity={selectedProducts[product.id] || 0}
                  subtitle={product.metafields?.bundle?.subtitle}
                  title={product.name}
                  available={isProductAvailable(
                    product,
                    shopConfiguration,
                    ignoreBaBOutOfStockAllowance,
                  )}
                  onIncrease={() => canAddProducts && addProduct(product)}
                  onDecrease={() => removeProduct(product)}
                  onCardClick={(productInfo) =>
                    onCardClick({
                      ...productInfo,
                      images: product.images,
                      productId: product.productId,
                      hasOneVariant: product.hasOnlyDefaultVariant,
                    })
                  }
                  description={product.description}
                  reviews={product?.reviews}
                />
              );
            })
          )}
        </SectionsSection>
      </div>,
    );
  }

  const sectionsMenu = Object.keys({
    ...productsSections,
    ...(others?.length && { [DEFAULT_SECTION]: [] }),
  }).map((section) => (
    <SectionTitleButton
      id={`button-${section}`}
      key={section}
      onClick={() => scrollToSection(section)}
    >
      {section === DEFAULT_SECTION ? t('bundles.additional_products') : section}
    </SectionTitleButton>
  ));

  return (
    <SectionsContainer>
      <NavSections className="bundle-section__nav">{sectionsMenu}</NavSections>
      {objectsInSections}
    </SectionsContainer>
  );
};

const SectionsContainer = styled.div.attrs({
  className: 'bundle-section-container',
})`
  display: block;
`;

const SectionsSection = styled.section.attrs({
  className: 'bundle-section',
})`
  width: 100%;
  max-width: 1010px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
  overflow-y: auto;
`;

const SectionTitle = styled(H3).attrs({
  className: 'bundle-section__title',
})`
  text-align: center;
  text-transform: uppercase;
  margin: 1rem 0;
`;

const SectionTitleButton = styled(Button)`
  text-transform: uppercase;
  font-weight: 400;
  &:hover {
    font-weight: 700;
  }

  &.showed {
    font-weight: 700;
  }
`;

const NavSections = styled.nav.attrs({
  className: 'bundle-section__nav',
})`
  position: sticky;
  inset: 0;
  top: -1px;
  width: 100%;
  margin: 0 auto;
  padding: 1rem 0;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  z-index: 1;

  @media (max-width: ${Breakpoints.sm}) {
    display: none;
  }
`;

const chunkProductsInSections = (
  products: GlobalVariant[] | GlobalProduct[],
): Record<string, GlobalVariant[] | GlobalProduct[]> => {
  const sections = {};
  products.forEach((product: GlobalProduct | GlobalVariant) => {
    const section = product.type;
    if (section === '') {
      if (sections[DEFAULT_SECTION]) {
        sections[DEFAULT_SECTION].push(product);
      } else {
        sections[DEFAULT_SECTION] = [product];
      }
    }
    if (section) {
      if (!sections[section]) {
        sections[section] = [];
      }
      sections[section].push(product);
    }
  });
  return sections;
};

const useTitlesOnScreen = (titles: string[] = []) => {
  useEffect(() => {
    titles.forEach((title) => {
      const titleElement = document.getElementById(`section-${title}`);
      const observer = getObserver(title);

      if (titleElement) {
        observer.observe(titleElement);
      }
      return () => {
        observer.unobserve(titleElement as Element);
      };
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // We only want to run this effect once

  return true;
};

const getObserver = (title: string, rootMargin = '0px') => {
  return typeof IntersectionObserver === 'function'
    ? new IntersectionObserver(
        ([entry]) => {
          const buttonTitle = document.getElementById(`button-${title}`);
          if (entry.isIntersecting) buttonTitle?.classList.add('showed');
          else buttonTitle?.classList.remove('showed');
        },
        {
          rootMargin,
          threshold: 0.2,
        },
      )
    : {
        observe: () => void {},
        unobserve: () => void {},
      };
};

const scrollToSection = (section: string) => {
  const sectionElement = document.getElementById(section);
  const scrollableElement = document.querySelector(
    '.bundle-scrollable-section',
  );
  if (sectionElement && scrollableElement) {
    // Top height of the element
    const elementHeightPosition = sectionElement.offsetTop;
    const stickyHeaderHeight = calculateStickyHeaderHeight();
    scrollableElement.scrollTo({
      top: elementHeightPosition - stickyHeaderHeight,
      behavior: 'smooth',
    });
  }
};

const calculateStickyHeaderHeight = () => {
  const headerSection = document.querySelector('.header-section');
  const navHeader = document.querySelector('.bundle-section__nav');
  const stickyHeader = document.querySelector(
    '.bundleapp-modal__container--complete-screen--header-container',
  );
  const stepperHeader = document.querySelector('.bundle-stepper-header');
  const productOptionsHeader = document.querySelector(
    '.awtomatic-bab-options-container',
  );
  const progressBar = document.querySelector('.awtomatic-progress-container');

  const headerSectionHeight = headerSection?.clientHeight || 0;
  const navHeaderHeight = navHeader?.clientHeight || 0;
  const stickyHeaderHeight = stickyHeader?.clientHeight || 0;
  const stepperHeaderHeight = stepperHeader?.clientHeight || 0;
  const productOptionsHeaderHeight = productOptionsHeader?.clientHeight || 0;
  const progressBarHeight = progressBar
    ? window.getComputedStyle(progressBar).order === '1'
      ? progressBar.clientHeight
      : 0
    : 0;

  return (
    progressBarHeight +
    headerSectionHeight +
    productOptionsHeaderHeight +
    stepperHeaderHeight +
    stickyHeaderHeight +
    navHeaderHeight +
    40
  ); // Add some extra space
};
